import { create } from 'zustand'
import type { ReactNode } from 'react'
import type { ModalState, ToastMessage } from '@/types'

interface UIState {
  // Modal state
  modal: ModalState
  
  // Toast notifications
  toasts: ToastMessage[]
  
  // Bottom sheet
  isBottomSheetOpen: boolean
  bottomSheetContent: ReactNode | null
  
  // Loading states
  globalLoading: boolean
  loadingMessage: string
  
  // Navigation
  currentRoute: string
  previousRoute: string
  
  // Actions
  openModal: (modal: Omit<ModalState, 'isOpen'>) => void
  closeModal: () => void
  
  addToast: (toast: Omit<ToastMessage, 'id'>) => void
  removeToast: (id: string) => void
  
  openBottomSheet: (content: React.ReactNode) => void
  closeBottomSheet: () => void
  
  setGlobalLoading: (loading: boolean, message?: string) => void
  
  setCurrentRoute: (route: string) => void
}

export const useUIStore = create<UIState>((set, get) => ({
  modal: { isOpen: false },
  toasts: [],
  isBottomSheetOpen: false,
  bottomSheetContent: null,
  globalLoading: false,
  loadingMessage: '',
  currentRoute: '',
  previousRoute: '',

  openModal: (modal) => set({
    modal: { isOpen: true, ...modal }
  }),

  closeModal: () => {
    const { modal } = get()
    modal.onClose?.()
    set({ modal: { isOpen: false } })
  },

  addToast: (toast) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newToast = { ...toast, id }
    
    set((state) => ({
      toasts: [...state.toasts, newToast]
    }))

    // Auto remove after duration
    setTimeout(() => {
      get().removeToast(id)
    }, toast.duration || 3000)
  },

  removeToast: (id) => set((state) => ({
    toasts: state.toasts.filter((t) => t.id !== id)
  })),

  openBottomSheet: (content) => set({
    isBottomSheetOpen: true,
    bottomSheetContent: content
  }),

  closeBottomSheet: () => set({
    isBottomSheetOpen: false,
    bottomSheetContent: null
  }),

  setGlobalLoading: (loading, message = '') => set({
    globalLoading: loading,
    loadingMessage: message
  }),

  setCurrentRoute: (route) => set((state) => ({
    previousRoute: state.currentRoute,
    currentRoute: route
  }))
}))

export default useUIStore
